import { h, setChildren } from "../core/dom.js";
import { icon } from "../lib/icons.js";
import { toast } from "./ui.js";

// Fullscreen camera capture with framing guides so progress photos stay consistent:
//  • a rule-of-thirds alignment grid, and
//  • an "onion-skin" ghost of the previous photo (the real consistency tool — line your
//    pose up with last time). Resolves the captured image via onCapture(blob|File).
// Falls back to the OS file/camera picker when getUserMedia isn't available or is denied.
export function openCamera({ ghostUrl = null, onCapture } = {}) {
  let stream = null;
  let facing = "environment";
  let ghostOpacity = 0.4;

  const video = h("video", { class: "cam-video", autoplay: true, muted: true });
  video.setAttribute("playsinline", ""); // iOS won't go fullscreen mid-capture
  video.muted = true;

  const ghostImg = ghostUrl
    ? h("img", { class: "cam-ghost", src: ghostUrl, alt: "", style: { opacity: ghostUrl ? ghostOpacity : 0 } })
    : null;
  let ghostVisible = !!ghostUrl;

  const stage = h("div", { class: "cam-stage" });

  // Hidden OS picker fallback (also offered explicitly as "Upload instead").
  const fileInput = h("input", {
    type: "file", accept: "image/*", capture: "environment", style: { display: "none" },
    onchange: (e) => { const f = e.target.files?.[0]; if (f) { finish(f); } },
  });

  const overlay = h("div", { class: "cam-overlay" }, fileInput);

  function gridOverlay() {
    return h("svg", { class: "cam-grid", viewBox: "0 0 100 100", preserveAspectRatio: "none", "aria-hidden": "true" },
      h("line", { x1: 50, y1: 0, x2: 50, y2: 100 }),
      h("line", { x1: 0, y1: 33.33, x2: 100, y2: 33.33 }),
      h("line", { x1: 0, y1: 66.66, x2: 100, y2: 66.66 })
    );
  }

  // ── Live view ────────────────────────────────────────────────────────────────
  function renderLive() {
    setChildren(stage, video, ghostVisible && ghostImg, gridOverlay());
    setChildren(overlay,
      fileInput,
      h("div", { class: "cam-top" },
        h("button", { class: "cam-icon-btn", "aria-label": "Close", onClick: close }, icon("x", { size: 24 })),
        h("button", { class: "cam-icon-btn", "aria-label": "Flip camera", onClick: flip }, icon("flip", { size: 22 }))
      ),
      stage,
      h("div", { class: "cam-bottom" },
        ghostUrl
          ? h("label", { class: "cam-ghost-ctrl" },
              h("button", { class: ["cam-chip", ghostVisible && "on"], onClick: toggleGhost }, "Ghost"),
              ghostVisible
                ? h("input", { type: "range", min: "0.1", max: "0.8", step: "0.05", value: String(ghostOpacity),
                    oninput: (e) => { ghostOpacity = parseFloat(e.target.value); if (ghostImg) ghostImg.style.opacity = ghostOpacity; } })
                : null
            )
          : h("div", { class: "dim", style: { fontSize: ".78rem", textAlign: "center" } }, "Line up centered, full body in frame"),
        h("div", { class: "cam-shutter-row" },
          h("button", { class: "cam-upload", onClick: () => fileInput.click() }, "Upload"),
          h("button", { class: "cam-shutter", "aria-label": "Take photo", onClick: capture }),
          h("span", { style: { width: "64px" } })
        )
      )
    );
  }

  function toggleGhost() {
    ghostVisible = !ghostVisible;
    renderLive();
  }

  async function start() {
    stop();
    if (!navigator.mediaDevices?.getUserMedia) return renderNoCamera("This browser can't open the camera here.");
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
      video.srcObject = stream;
      video.classList.toggle("mirror", facing === "user");
      await video.play().catch(() => {});
      renderLive();
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Camera permission was blocked. Allow it in your browser, or upload a photo instead."
        : "Couldn't start the camera. You can upload a photo instead.";
      renderNoCamera(msg);
    }
  }

  function flip() {
    facing = facing === "environment" ? "user" : "environment";
    start();
  }

  // ── Capture → preview → confirm ───────────────────────────────────────────────
  function capture() {
    const w = video.videoWidth, ht = video.videoHeight;
    if (!w || !ht) { toast("Camera still warming up…", "warn"); return; }
    const maxSide = 1280; // keep the JPEG small enough to sit comfortably on-device
    const scale = Math.min(1, maxSide / Math.max(w, ht));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(ht * scale);
    const ctx = canvas.getContext("2d");
    if (facing === "user") { ctx.translate(canvas.width, 0); ctx.scale(-1, 1); } // match mirrored preview
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => { if (blob) showPreview(blob); else toast("Capture failed", "error"); }, "image/jpeg", 0.85);
  }

  function showPreview(blob) {
    stop(); // freeze the camera while reviewing
    const url = URL.createObjectURL(blob);
    const preview = h("img", { class: "cam-preview", src: url, alt: "Captured photo" });
    setChildren(overlay,
      h("div", { class: "cam-top" },
        h("button", { class: "cam-icon-btn", "aria-label": "Close", onClick: close }, icon("x", { size: 24 }))
      ),
      h("div", { class: "cam-stage" }, preview),
      h("div", { class: "cam-bottom" },
        h("div", { class: "cam-shutter-row", style: { gap: "12px" } },
          h("button", { class: "btn btn-ghost grow", onClick: () => { URL.revokeObjectURL(url); start(); } }, "Retake"),
          h("button", { class: "btn btn-primary grow", onClick: () => { URL.revokeObjectURL(url); finish(blob); } }, icon("check", { size: 18 }), "Use photo")
        )
      )
    );
  }

  function renderNoCamera(message) {
    setChildren(overlay,
      fileInput,
      h("div", { class: "cam-top" },
        h("button", { class: "cam-icon-btn", "aria-label": "Close", onClick: close }, icon("x", { size: 24 }))
      ),
      h("div", { class: "cam-stage cam-stage-msg" },
        h("div", { class: "center", style: { padding: "0 28px" } },
          h("div", { style: { marginBottom: "16px", opacity: 0.7 } }, icon("camera", { size: 40 })),
          h("p", { style: { color: "#fff" } }, message),
          h("button", { class: "btn btn-primary mt", onClick: () => fileInput.click() }, "Upload a photo")
        )
      )
    );
  }

  function stop() {
    if (stream) { stream.getTracks().forEach((t) => t.stop()); stream = null; }
  }

  function close() {
    stop();
    document.removeEventListener("keydown", onKey);
    overlay.remove();
  }

  function finish(imageBlob) {
    close();
    onCapture?.(imageBlob);
  }

  const onKey = (e) => { if (e.key === "Escape") close(); };
  document.addEventListener("keydown", onKey);

  document.body.appendChild(overlay);
  start();
  return { close };
}
